import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type {
  GenerateRacksReport,
  LocationImportReport,
  LocationKindDef,
  LocationNode,
  LocationToken,
} from './locations.types';

const BASE = '/v1/locations';
const KEY = ['locations'] as const;

/** An asked refresh of the tree (and whatever else the page has open). */
export function useRefreshLocations() {
  const qc = useQueryClient();
  return () => qc.refetchQueries({ queryKey: KEY, type: 'active' });
}

export function useLocations(includeInactive = false) {
  return useQuery({
    queryKey: [...KEY, { includeInactive }],
    queryFn: () => api.get<LocationNode[]>(`${BASE}?include_inactive=${includeInactive}`),
  });
}

/** R-120: the active token vocabulary, for client-side segment validation/hints. */
export function useLocationTokens() {
  return useQuery({
    queryKey: [...KEY, 'tokens'],
    queryFn: () => api.get<LocationToken[]>(`${BASE}/tokens`),
  });
}

/** R-149 (D-114): the seeded room-kind taxonomy — drives the Kind control
 * (kinds valid for a node's loc_type, labelled per locale). */
export function useLocationKinds() {
  return useQuery({
    queryKey: [...KEY, 'kinds'],
    queryFn: () => api.get<LocationKindDef[]>(`${BASE}/kinds`),
  });
}

export function useLocation(code: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', code],
    queryFn: () => api.get<LocationNode>(`${BASE}/${code}`),
    enabled: !!code,
  });
}

export function useUpsertLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (node: Partial<LocationNode>) => api.post<LocationNode>(BASE, node),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSetLocationActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, active }: { code: string; active: boolean }) =>
      api.post<boolean>(`${BASE}/${code}/active`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-234: mark a location as a stockroom (a "store"), or clear the flag.
 *
 * ⚠ **Deliberately NOT part of the upsert.** That handler is also the CSV import
 * path, and a taxonomy re-import carries no stockroom column — riding the upsert
 * would silently un-flag every store on the next import. It is its own route for
 * the same reason it is its own control on the form.
 */
export function useSetStockroom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, isStockroom }: { code: string; isStockroom: boolean }) =>
      api.post<LocationNode>(`${BASE}/${code}/stockroom`, { is_stockroom: isStockroom }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-350 (D-175): name the plumbing room whose loop waters a grow room, or clear it
 * (`fedByCode: null`).
 *
 * ⚠ Its own route, never the upsert — the upsert is also the CSV re-import path and a
 * taxonomy re-import carries no loop column; riding it would silently unmap every loop.
 * Same rule, same shape as `useSetStockroom` above. Mirrors `POST /v1/locations/{code}/fed-by`.
 */
export function useSetFedBy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, fedByCode }: { code: string; fedByCode: string | null }) =>
      api.post<LocationNode>(`${BASE}/${code}/fed-by`, { fed_by_code: fedByCode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-189: generate a grow room's rack nodes from a count. `dryRun` reports what
 * WOULD be written without writing it. Only invalidates on a real run — a dry
 * run changes nothing, so refetching the tree would be pure noise. */
export function useGenerateRacks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, racks, dryRun }: { code: string; racks: number; dryRun: boolean }) =>
      api.post<GenerateRacksReport>(`${BASE}/${code}/racks`, { racks, dry_run: dryRun }),
    onSuccess: (_r, vars) => {
      if (!vars.dryRun) qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useImportLocations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, dryRun }: { csv: string; dryRun: boolean }) =>
      api.post<LocationImportReport>(`${BASE}/import?dry_run=${dryRun}`, csv, {
        headers: { 'Content-Type': 'text/csv' },
      }),
    onSuccess: (_r, vars) => {
      if (!vars.dryRun) qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
